import {
  Autocomplete,
  FormControl,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
} from "@mui/material";
import boxShadow from "assets/theme/functions/boxShadow";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { uniqBy } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DatePicker } from "rsuite";
import { getPlaces } from "store/slice/places/placeSlice";

const glassStyle = {
  // background: "rgba(255, 255, 255, 0.1)",
  // boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
  boxShadow: 2,
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  padding: "24px",
  color: "#fff",
};

function RevenueCard({ onFilterChange, revenueData = {}, hasSubmitted }) {
  const dispatch = useDispatch();
  const allPlaces = useSelector((state) => state.places?.placesList);
  const currentDate = moment().toDate();

  const timeZones = uniqBy(allPlaces, "timeZoneId").map(
    (p) => p?.timeZoneId || ""
  );
  timeZones.push("Asia/Kolkata", "UTC");

  const [filterOptions, setFilterOptions] = useState({
    tz: "UTC",
    placeId: [],
    startDate: moment(currentDate).startOf("day"),
    endDate: moment(currentDate).endOf("day"),
  });

  useEffect(() => {
    dispatch(getPlaces())
      .unwrap()
      .then((res) => {
        const firstPlaceId = res?.places?.[0]?._id;
        const updated = {
          ...filterOptions,
          placeId: [firstPlaceId],
        };
        setFilterOptions(updated);
        onFilterChange(updated);
      });
  }, []);

  const handleChange = (key, value) => {
    const updated = { ...filterOptions, [key]: value };
    setFilterOptions(updated);
    onFilterChange(updated);
  };

  const showError = (field) => {
    if (!hasSubmitted) return false;
    if (field === "startDate") return !filterOptions.startDate;
    if (field === "endDate") return !filterOptions.endDate;
    if (field === "placeId") return filterOptions.placeId.length === 0;
    if (field === "tz") return !filterOptions.tz;
  };

  return (
    <Box sx={{ ...glassStyle }}>
      <Grid container spacing={3}>
        {/* Date Picker */}
        <Grid item xs={12} md={6}>
          <MDTypography variant="caption">Select Start Date</MDTypography>
          <FormControl fullWidth error={showError("startDate")}>
            <DatePicker
              id="startDate"
              placeholder="Start Date"
              size="md"
              format="MM/dd/yyyy hh:mm a"
              showMeridiem
              value={filterOptions.startDate?.toDate()}
              onChange={(val) => handleChange("startDate", moment(val))}
              onClean={() => handleChange("startDate", null)}
            />
            {showError("startDate") && (
              <Typography variant="caption" color="error">
                Start Date is required
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <MDTypography variant="caption">Select End Date</MDTypography>
          <FormControl fullWidth error={showError("endDate")}>
            <DatePicker
              id="endDate"
              placeholder="End Date"
              size="md"
              format="MM/dd/yyyy hh:mm a"
              showMeridiem
              value={filterOptions.endDate?.toDate()}
              onChange={(val) => handleChange("endDate", moment(val))}
              onClean={() => handleChange("endDate", null)}
            />
            {showError("endDate") && (
              <Typography variant="caption" color="error">
                End Date is required
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Place Selector */}
        <Grid item xs={12}>
          <FormControl fullWidth error={showError("placeId")}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={allPlaces.length > 1 ? ["All", ...allPlaces] : allPlaces}
              getOptionLabel={(option) =>
                option === "All" ? "All Places" : option?.parkingCode || ""
              }
              renderOption={(props, option) => (
                <li {...props}>
                  {option === "All"
                    ? "All Places"
                    : `${option?.parkingCode} - ${
                        option?.google?.formatted_address || "No address"
                      }`}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={
                      option === "All" ? "All Places" : option?.parkingCode
                    }
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              name="placeIds"
              size="small"
              disableClearable
              value={
                filterOptions.placeId.length === allPlaces.length
                  ? allPlaces
                  : allPlaces.filter((place) =>
                      filterOptions.placeId.includes(place?._id)
                    )
              }
              onChange={(_, newValue) => {
                const placeIds = newValue.includes("All")
                  ? allPlaces.map((p) => p._id)
                  : newValue.map((p) => p._id);
                handleChange("placeId", placeIds);
              }}
              renderInput={(params) => (
                <MDInput label="Place" fullWidth {...params} />
              )}
            />
            {showError("placeId") && (
              <Typography variant="caption" color="error">
                At least one place is required
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Timezone Selector */}
        <Grid item xs={12}>
          <FormControl fullWidth error={showError("tz")}>
            <Autocomplete
              disableClearable
              name="tz"
              value={filterOptions.tz}
              options={timeZones}
              onChange={(e, val) => handleChange("tz", val)}
              renderInput={(params) => (
                <MDInput label="Select Timezone" fullWidth {...params} />
              )}
            />
            {showError("tz") && (
              <Typography variant="caption" color="error">
                Timezone is required
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Revenue Results */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: "rgba(255,255,255,0.08)",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Grid container spacing={1}>
              {Object.entries(revenueData).map(([key, value], idx) => (
                <Grid item xs={12} key={idx}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    bgcolor="rgba(255,255,255,0.05)"
                    px={2}
                    py={1}
                    borderRadius={1}
                    mb={1}
                  >
                    <Typography sx={{ color: "text" }}>{key}</Typography>
                    <Typography fontWeight="bold" sx={{ color: "text" }}>
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default RevenueCard;
